/**
 * Display Localizations View
 *
 * Story 3.10: Episode Display Localizations
 * Allows admins to manage display localizations for episodes:
 * - View existing localizations with language badges
 * - Generate new localizations using AI
 * - Edit/Delete existing localizations
 * - Preview localized content before saving
 */

import { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useEpisode, useUpdateEpisode, type DictionaryEntry } from '../services/episodesApi';
import { EpisodeJsonImport } from '../components/EpisodeJsonImport';
import { transformLocalizationForApi } from '../utils/episodeExportImport';
import {
  useDisplayLocalizations,
  useCreateDisplayLocalization,
  useUpdateDisplayLocalization,
  useDeleteDisplayLocalization,
  useGenerateDisplayLocalization,
  useGenerateBulkDisplayLocalizations,
  type DisplayLocalizationResponse,
  type DisplayLocalizationCreate,
  type DisplayLocalizationUpdate,
  type GenerateResponse,
  type LocalizedPersona,
} from '../services/displayLocalizationApi';
import { useInitialData } from 'contexts/InitialDataContext';

// Existing localized dictionary entry type (from episode.dictionary[*].localized)
interface ExistingLocalizedDictEntry {
  word: string;
  explanation: string;
  examplePhrases: string[];
}

// Original dictionary entry (from episode.dictionary)
interface OriginalDictEntry {
  word: string;
  explanation: string;
  examplePhrases: string[];
}

// Pending localization (staged but not saved to API)
interface PendingLocalization {
  languageCode: string;
  localizedName: string;
  localizedContext: string;
  localizedGoals: string[];
  localizedPersonas: LocalizedPersona[];
  dictionaryUpdates?: ExistingLocalizedDictEntry[];
}

// Modal for editing/creating localizations
interface LocalizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called when saving - for editing existing saved localizations */
  onSave: (
    data: DisplayLocalizationCreate | DisplayLocalizationUpdate,
    dictionaryUpdates?: ExistingLocalizedDictEntry[]
  ) => Promise<void>;
  /** Called when staging - for adding to pending (new/generated localizations) */
  onStage?: (data: PendingLocalization) => void;
  localization?: DisplayLocalizationResponse;
  /** Pending localization being edited */
  pendingLocalization?: PendingLocalization;
  generatedContent?: GenerateResponse;
  existingDictionary?: ExistingLocalizedDictEntry[];
  originalDictionary?: OriginalDictEntry[];
  languageCode: string;
  languageName: string;
  isLoading: boolean;
  /** true if editing an existing saved localization */
  isEdit: boolean;
  /** true if editing a pending (unsaved) localization */
  isPending: boolean;
}

function LocalizationModal({
  isOpen,
  onClose,
  onSave,
  onStage,
  localization,
  pendingLocalization,
  generatedContent,
  existingDictionary,
  originalDictionary,
  languageCode,
  languageName,
  isLoading,
  isEdit,
  isPending,
}: LocalizationModalProps) {
  const initialData = localization || pendingLocalization || generatedContent;

  const [localizedName, setLocalizedName] = useState(initialData?.localizedName || '');
  const [localizedContext, setLocalizedContext] = useState(initialData?.localizedContext || '');
  const [localizedGoals, setLocalizedGoals] = useState<string[]>(
    initialData?.localizedGoals || ['']
  );
  const [localizedPersonas, setLocalizedPersonas] = useState<LocalizedPersona[]>(
    initialData?.localizedPersonas || [{ name: '', description: '' }]
  );

  // Initialize dictionary with existing translations or empty entries for each original word
  const initializeDictionary = () => {
    if (generatedContent?.localizedDictionary) {
      return generatedContent.localizedDictionary;
    }
    if (existingDictionary && existingDictionary.length > 0) {
      return existingDictionary;
    }
    // Create empty entries for original dictionary words
    if (originalDictionary && originalDictionary.length > 0) {
      return originalDictionary.map((entry) => ({
        word: entry.word,
        explanation: '',
        examplePhrases: entry.examplePhrases.map(() => ''),
      }));
    }
    return [];
  };

  const [localizedDictionary, setLocalizedDictionary] =
    useState<ExistingLocalizedDictEntry[]>(initializeDictionary);

  // Reset form when modal opens with new data
  useEffect(() => {
    if (isOpen) {
      setLocalizedName(initialData?.localizedName || '');
      setLocalizedContext(initialData?.localizedContext || '');
      setLocalizedGoals(initialData?.localizedGoals || ['']);
      setLocalizedPersonas(initialData?.localizedPersonas || [{ name: '', description: '' }]);
      setLocalizedDictionary(initializeDictionary());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    initialData,
    generatedContent,
    pendingLocalization,
    existingDictionary,
    originalDictionary,
  ]);

  if (!isOpen) return null;

  const handleGoalChange = (index: number, value: string) => {
    const newGoals = [...localizedGoals];
    newGoals[index] = value;
    setLocalizedGoals(newGoals);
  };

  const handlePersonaChange = (index: number, field: 'name' | 'description', value: string) => {
    const newPersonas = [...localizedPersonas];
    newPersonas[index] = { ...newPersonas[index], [field]: value };
    setLocalizedPersonas(newPersonas);
  };

  const handleDictionaryExplanationChange = (index: number, value: string) => {
    const newDictionary = [...localizedDictionary];
    newDictionary[index] = { ...newDictionary[index], explanation: value };
    setLocalizedDictionary(newDictionary);
  };

  const handleDictionaryExampleChange = (
    entryIndex: number,
    phraseIndex: number,
    value: string
  ) => {
    const newDictionary = [...localizedDictionary];
    const newPhrases = [...newDictionary[entryIndex].examplePhrases];
    newPhrases[phraseIndex] = value;
    newDictionary[entryIndex] = { ...newDictionary[entryIndex], examplePhrases: newPhrases };
    setLocalizedDictionary(newDictionary);
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!localizedName.trim()) {
      toast.error('Localized name is required');
      return;
    }
    if (!localizedContext.trim()) {
      toast.error('Localized context is required');
      return;
    }
    if (localizedGoals.some((g) => !g.trim())) {
      toast.error('All goals must have content');
      return;
    }
    if (localizedPersonas.some((p) => !p.name.trim() || !p.description.trim())) {
      toast.error('All personas must have name and description');
      return;
    }

    // Filter dictionary entries that have content (at least explanation filled)
    const dictionaryWithContent = localizedDictionary.filter(
      (entry) => entry.explanation.trim() !== ''
    );

    // If editing an existing saved localization, save directly to API
    if (isEdit && !isPending) {
      const data: DisplayLocalizationUpdate = {
        localizedName: localizedName.trim(),
        localizedContext: localizedContext.trim(),
        localizedGoals: localizedGoals.map((g) => g.trim()),
        localizedPersonas: localizedPersonas.map((p) => ({
          name: p.name.trim(),
          description: p.description.trim(),
        })),
      };
      await onSave(data, dictionaryWithContent.length > 0 ? dictionaryWithContent : undefined);
    } else {
      // For new or pending localizations, stage them (add to pending)
      const pendingData: PendingLocalization = {
        languageCode,
        localizedName: localizedName.trim(),
        localizedContext: localizedContext.trim(),
        localizedGoals: localizedGoals.map((g) => g.trim()),
        localizedPersonas: localizedPersonas.map((p) => ({
          name: p.name.trim(),
          description: p.description.trim(),
        })),
        dictionaryUpdates: dictionaryWithContent.length > 0 ? dictionaryWithContent : undefined,
      };
      onStage?.(pendingData);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">
              {isEdit ? 'Edit' : 'Add'} Display Localization - {languageName}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
              data-testid="close-modal"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            {/* Localized Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Localized Episode Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={localizedName}
                onChange={(e) => setLocalizedName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Episode name in target language"
                data-testid="localized-name-input"
              />
            </div>

            {/* Localized Context */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Localized Context <span className="text-red-500">*</span>
              </label>
              <textarea
                value={localizedContext}
                onChange={(e) => setLocalizedContext(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Episode context in target language"
                data-testid="localized-context-input"
              />
            </div>

            {/* Localized Goals */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Localized Goals <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                {localizedGoals.map((goal, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={goal}
                      onChange={(e) => handleGoalChange(index, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={`Goal ${index + 1}`}
                      data-testid={`localized-goal-${index}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Localized Personas */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Localized Personas <span className="text-red-500">*</span>
              </label>
              <div className="space-y-3">
                {localizedPersonas.map((persona, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={persona.name}
                        onChange={(e) => handlePersonaChange(index, 'name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={`Persona ${index + 1} name`}
                        data-testid={`localized-persona-name-${index}`}
                      />
                      <textarea
                        value={persona.description}
                        onChange={(e) => handlePersonaChange(index, 'description', e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={`Persona ${index + 1} description`}
                        data-testid={`localized-persona-description-${index}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Localized Dictionary - show if there are dictionary entries to translate */}
            {localizedDictionary.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Localized Dictionary{' '}
                  <span className="text-xs text-gray-500 font-normal">
                    (translate explanations and example sentences to {languageName})
                  </span>
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Users will see both the original sentences (for learning) and your translations
                  (for understanding).
                </p>
                <div className="space-y-3">
                  {localizedDictionary.map((entry, index) => (
                    <div
                      key={index}
                      className="border border-green-200 rounded-lg p-3 bg-green-50"
                      data-testid={`localized-dictionary-${index}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium text-gray-800">{entry.word}</span>
                        {originalDictionary?.[index] && (
                          <span className="text-xs text-gray-500">
                            — {originalDictionary[index].explanation}
                          </span>
                        )}
                      </div>
                      <div className="space-y-2">
                        <div>
                          <label className="text-xs text-gray-600 mb-1 block">
                            Explanation in {languageName}:
                          </label>
                          <input
                            type="text"
                            value={entry.explanation}
                            onChange={(e) =>
                              handleDictionaryExplanationChange(index, e.target.value)
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                            placeholder={`Translate: "${originalDictionary?.[index]?.explanation || ''}"`}
                            data-testid={`dict-explanation-${index}`}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-600 mb-1 block">
                            Translate example sentences to {languageName}:
                          </label>
                          {entry.examplePhrases.map((phrase, phraseIndex) => (
                            <div key={phraseIndex} className="mb-2">
                              <div className="text-xs text-gray-500 mb-1 italic">
                                "
                                {originalDictionary?.[index]?.examplePhrases[phraseIndex] ||
                                  `Example ${phraseIndex + 1}`}
                                "
                              </div>
                              <input
                                type="text"
                                value={phrase}
                                onChange={(e) =>
                                  handleDictionaryExampleChange(index, phraseIndex, e.target.value)
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                                placeholder={`Translation in ${languageName}`}
                                data-testid={`dict-example-${index}-${phraseIndex}`}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400"
              data-testid="save-localization-button"
            >
              {isLoading
                ? 'Saving...'
                : isEdit && !isPending
                  ? 'Update'
                  : isPending
                    ? 'Update Draft'
                    : 'Add to Drafts'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Delete confirmation modal
interface DeleteConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  languageName: string;
  isLoading: boolean;
}

function DeleteConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  languageName,
  isLoading,
}: DeleteConfirmModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Delete Localization</h2>
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete the {languageName} localization? This action cannot be
          undone.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-400"
            data-testid="confirm-delete-button"
          >
            {isLoading ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function DisplayLocalizationsView() {
  const { episodeId } = useParams<{ episodeId: string }>();
  const { state: initialDataState } = useInitialData();

  // Episode data
  const {
    data: episode,
    isLoading: isLoadingEpisode,
    refetch: refetchEpisode,
  } = useEpisode(episodeId);

  // Localizations data
  const { data: localizations = [], isLoading: isLoadingLocalizations } =
    useDisplayLocalizations(episodeId);

  // Mutations
  const createMutation = useCreateDisplayLocalization();
  const updateMutation = useUpdateDisplayLocalization();
  const deleteMutation = useDeleteDisplayLocalization();
  const generateMutation = useGenerateDisplayLocalization();
  const generateBulkMutation = useGenerateBulkDisplayLocalizations();

  // Modal state
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingLocalization, setEditingLocalization] =
    useState<DisplayLocalizationResponse | null>(null);
  const [generatedContent, setGeneratedContent] = useState<GenerateResponse | null>(null);
  const [localizationToDelete, setLocalizationToDelete] =
    useState<DisplayLocalizationResponse | null>(null);

  // Generate all state
  const [isGeneratingAll, setIsGeneratingAll] = useState(false);

  // Story 11.19: Import localizations state
  const [isImporting, setIsImporting] = useState(false);

  // Pending (unsaved) localizations - generated or manually added but not yet saved
  const [pendingLocalizations, setPendingLocalizations] = useState<
    Record<string, PendingLocalization>
  >({});

  // Track which pending localization is being edited
  const [editingPendingLanguage, setEditingPendingLanguage] = useState<string | null>(null);

  // Is saving all pending localizations
  const [isSavingAll, setIsSavingAll] = useState(false);

  // Get available languages for display localization
  const availableLanguages = useMemo(() => {
    const languages = initialDataState.data?.languages || [];
    const existingCodes = localizations.map((l) => l.languageCode);
    const pendingCodes = Object.keys(pendingLocalizations);

    // Exclude: episode's own language, already localized, pending, and "Other" (zxx)
    return languages.filter(
      (lang) =>
        lang.code !== 'zxx' &&
        lang.code !== episode?.languageCode &&
        !existingCodes.includes(lang.code) &&
        !pendingCodes.includes(lang.code)
    );
  }, [
    initialDataState.data?.languages,
    localizations,
    pendingLocalizations,
    episode?.languageCode,
  ]);

  // Get language name by code
  const getLanguageName = (code: string): string => {
    const lang = initialDataState.data?.languages?.find((l) => l.code === code);
    return lang?.name || code.toUpperCase();
  };

  // Handle generate localization - adds to pending, doesn't save
  const handleGenerate = async () => {
    if (!selectedLanguage || !episodeId) return;

    try {
      const generated = await generateMutation.mutateAsync({
        episodeId,
        languageCode: selectedLanguage,
      });

      // Add to pending localizations (not saved yet)
      const pendingLoc: PendingLocalization = {
        languageCode: selectedLanguage,
        localizedName: generated.localizedName,
        localizedContext: generated.localizedContext,
        localizedGoals: generated.localizedGoals,
        localizedPersonas: generated.localizedPersonas,
        dictionaryUpdates:
          generated.localizedDictionary.length > 0 ? generated.localizedDictionary : undefined,
      };

      setPendingLocalizations((prev) => ({
        ...prev,
        [selectedLanguage]: pendingLoc,
      }));

      setSelectedLanguage('');
      toast.success(
        `Generated ${getLanguageName(selectedLanguage)} localization. Click "Save All" to save.`
      );
    } catch (error) {
      console.error('Failed to generate localization:', error);
      toast.error('Failed to generate localization. Please try again.');
    }
  };

  // Handle generate all localizations (single LLM call) - adds to pending, doesn't save
  const handleGenerateAll = async () => {
    if (!episodeId || availableLanguages.length === 0) return;

    setIsGeneratingAll(true);
    const languageCodes = availableLanguages.map((lang) => lang.code);

    try {
      // Generate all localizations in a single LLM call
      const result = await generateBulkMutation.mutateAsync({
        episodeId,
        languageCodes,
      });

      // Add all generated localizations to pending (not saved yet)
      const newPending: Record<string, PendingLocalization> = {};
      for (const [, localization] of Object.entries(result.localizations)) {
        newPending[localization.languageCode] = {
          languageCode: localization.languageCode,
          localizedName: localization.localizedName,
          localizedContext: localization.localizedContext,
          localizedGoals: localization.localizedGoals,
          localizedPersonas: localization.localizedPersonas,
          dictionaryUpdates:
            localization.localizedDictionary?.length > 0
              ? localization.localizedDictionary
              : undefined,
        };
      }

      setPendingLocalizations((prev) => ({
        ...prev,
        ...newPending,
      }));

      const generatedCount = Object.keys(newPending).length;
      const failedCount = result.failedLanguages.length;

      if (generatedCount > 0 && failedCount === 0) {
        toast.success(`Generated ${generatedCount} localizations. Click "Save All" to save.`);
      } else if (generatedCount > 0 && failedCount > 0) {
        toast.success(
          `Generated ${generatedCount} localizations, ${failedCount} failed. Click "Save All" to save.`
        );
      } else {
        toast.error('Failed to generate localizations. Please try again.');
      }
    } catch (error) {
      console.error('Failed to generate bulk localizations:', error);
      toast.error('Failed to generate localizations. Please try again.');
    } finally {
      setIsGeneratingAll(false);
    }
  };

  // Handle edit localization
  const handleEdit = (localization: DisplayLocalizationResponse) => {
    setEditingLocalization(localization);
    setGeneratedContent(null);
    setSelectedLanguage(localization.languageCode);
    setIsModalOpen(true);
  };

  // Handle delete localization
  const handleDeleteClick = (localization: DisplayLocalizationResponse) => {
    setLocalizationToDelete(localization);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!localizationToDelete || !episodeId) return;

    try {
      await deleteMutation.mutateAsync({
        episodeId,
        languageCode: localizationToDelete.languageCode,
      });
      toast.success('Localization deleted successfully!');
      setIsDeleteModalOpen(false);
      setLocalizationToDelete(null);
    } catch (error) {
      console.error('Failed to delete localization:', error);
      toast.error('Failed to delete localization. Please try again.');
    }
  };

  // Handle editing a pending localization
  const handleEditPending = (languageCode: string) => {
    setEditingPendingLanguage(languageCode);
    setSelectedLanguage(languageCode);
    setEditingLocalization(null);
    setGeneratedContent(null);
    setIsModalOpen(true);
  };

  // Handle deleting a pending localization
  const handleDeletePending = (languageCode: string) => {
    setPendingLocalizations((prev) => {
      const next = { ...prev };
      delete next[languageCode];
      return next;
    });
    toast.success(`Removed ${getLanguageName(languageCode)} draft.`);
  };

  // Handle adding a localization manually (opens empty modal)
  const handleAddManually = () => {
    if (!selectedLanguage) {
      toast.error('Please select a language first');
      return;
    }
    setEditingPendingLanguage(null);
    setEditingLocalization(null);
    setGeneratedContent(null);
    setIsModalOpen(true);
  };

  // Handle staging a localization (add to pending from modal)
  const handleStage = (data: PendingLocalization) => {
    setPendingLocalizations((prev) => ({
      ...prev,
      [data.languageCode]: data,
    }));
    setIsModalOpen(false);
    setEditingPendingLanguage(null);
    setSelectedLanguage('');
    toast.success(
      `${getLanguageName(data.languageCode)} localization staged. Click "Save All" to save.`
    );
  };

  // Handle saving all pending localizations
  const handleSaveAll = async () => {
    if (!episodeId || Object.keys(pendingLocalizations).length === 0) return;

    setIsSavingAll(true);
    let successCount = 0;
    let errorCount = 0;

    for (const pending of Object.values(pendingLocalizations)) {
      try {
        // Check if localization already exists (update) or is new (create)
        const existing = localizations.find((l) => l.languageCode === pending.languageCode);

        if (existing) {
          await updateMutation.mutateAsync({
            episodeId,
            languageCode: pending.languageCode,
            update: {
              localizedName: pending.localizedName,
              localizedContext: pending.localizedContext,
              localizedGoals: pending.localizedGoals,
              localizedPersonas: pending.localizedPersonas,
            },
          });
        } else {
          await createMutation.mutateAsync({
            episodeId,
            localization: {
              languageCode: pending.languageCode,
              localizedName: pending.localizedName,
              localizedContext: pending.localizedContext,
              localizedGoals: pending.localizedGoals,
              localizedPersonas: pending.localizedPersonas,
            },
          });
        }

        // Save dictionary translations if provided
        if (
          pending.dictionaryUpdates &&
          pending.dictionaryUpdates.length > 0 &&
          episode?.dictionary
        ) {
          const updatedDictionary: DictionaryEntry[] = episode.dictionary.map((entry) => {
            const update = pending.dictionaryUpdates?.find((u) => u.word === entry.word);
            if (update) {
              return {
                ...entry,
                localized: {
                  ...(entry.localized || {}),
                  [pending.languageCode]: {
                    explanation: update.explanation,
                    examplePhrases: update.examplePhrases,
                  },
                },
              };
            }
            return entry;
          });

          await updateEpisodeMutation.mutateAsync({
            episodeId,
            update: { dictionary: updatedDictionary },
          });
        }

        successCount++;
      } catch (error) {
        console.error(`Failed to save localization for ${pending.languageCode}:`, error);
        errorCount++;
      }
    }

    // Refetch episode to update dictionary in UI
    await refetchEpisode();

    // Clear all pending on success
    if (errorCount === 0) {
      setPendingLocalizations({});
    }

    setIsSavingAll(false);

    if (successCount > 0 && errorCount === 0) {
      toast.success(
        `Saved ${successCount} localization${successCount === 1 ? '' : 's'} successfully!`
      );
    } else if (successCount > 0 && errorCount > 0) {
      toast.success(
        `Saved ${successCount} localization${successCount === 1 ? '' : 's'}, ${errorCount} failed.`
      );
    } else {
      toast.error('Failed to save localizations. Please try again.');
    }
  };

  /**
   * Story 11.19: Handle import localizations from JSON file
   * Creates or updates localizations directly via API
   */
  const handleLocalizationsImport = async (
    importedLocalizations: Array<{
      language_code: string;
      localized_name: string;
      localized_context: string;
      localized_goals: string[];
      localized_personas: Array<{ name: string; description: string }>;
    }>
  ) => {
    if (!episodeId) return;

    setIsImporting(true);
    let successCount = 0;
    let errorCount = 0;

    for (const loc of importedLocalizations) {
      const transformed = transformLocalizationForApi(loc);
      const existingLocalization = localizations.find(
        (l) => l.languageCode === transformed.languageCode
      );

      try {
        if (existingLocalization) {
          // Update existing localization
          await updateMutation.mutateAsync({
            episodeId,
            languageCode: transformed.languageCode,
            update: {
              localizedName: transformed.localizedName,
              localizedContext: transformed.localizedContext,
              localizedGoals: transformed.localizedGoals,
              localizedPersonas: transformed.localizedPersonas,
            },
          });
        } else {
          // Create new localization
          await createMutation.mutateAsync({
            episodeId,
            localization: transformed,
          });
        }
        successCount++;
      } catch (error) {
        console.error(`Failed to import localization for ${loc.language_code}:`, error);
        errorCount++;
      }
    }

    setIsImporting(false);

    if (successCount > 0 && errorCount === 0) {
      toast.success(
        `Imported ${successCount} localization${successCount === 1 ? '' : 's'} successfully.`
      );
    } else if (successCount > 0 && errorCount > 0) {
      toast.success(
        `Imported ${successCount} localization${successCount === 1 ? '' : 's'}, ${errorCount} failed.`
      );
    } else {
      toast.error('Failed to import localizations. Please try again.');
    }
  };

  // Handle save (create or update)
  // Episode update mutation for saving dictionary translations
  const updateEpisodeMutation = useUpdateEpisode();

  const handleSave = async (
    data: DisplayLocalizationCreate | DisplayLocalizationUpdate,
    dictionaryUpdates?: ExistingLocalizedDictEntry[]
  ) => {
    if (!episodeId) return;

    const targetLanguageCode = editingLocalization?.languageCode || selectedLanguage;

    try {
      // Save display localization
      if (editingLocalization) {
        await updateMutation.mutateAsync({
          episodeId,
          languageCode: editingLocalization.languageCode,
          update: data as DisplayLocalizationUpdate,
        });
      } else {
        await createMutation.mutateAsync({
          episodeId,
          localization: data as DisplayLocalizationCreate,
        });
      }

      // Save dictionary translations if provided
      if (dictionaryUpdates && dictionaryUpdates.length > 0 && episode?.dictionary) {
        const updatedDictionary: DictionaryEntry[] = episode.dictionary.map((entry) => {
          const update = dictionaryUpdates.find((u) => u.word === entry.word);
          if (update) {
            return {
              ...entry,
              localized: {
                ...(entry.localized || {}),
                [targetLanguageCode]: {
                  explanation: update.explanation,
                  examplePhrases: update.examplePhrases,
                },
              },
            };
          }
          return entry;
        });

        await updateEpisodeMutation.mutateAsync({
          episodeId,
          update: { dictionary: updatedDictionary },
        });
      }

      toast.success(
        editingLocalization
          ? 'Localization updated successfully!'
          : 'Localization created successfully!'
      );
      setIsModalOpen(false);
      setEditingLocalization(null);
      setGeneratedContent(null);
      setSelectedLanguage('');
    } catch (error) {
      console.error('Failed to save localization:', error);
      toast.error('Failed to save localization. Please try again.');
    }
  };

  // Close modal and reset state
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingLocalization(null);
    setEditingPendingLanguage(null);
    setGeneratedContent(null);
  };

  if (isLoadingEpisode || isLoadingLocalizations) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Helper to get localized dictionary for a specific language
  const getLocalizedDictionary = (languageCode: string) => {
    if (!episode?.dictionary) return [];
    return episode.dictionary
      .filter((entry) => entry.localized?.[languageCode])
      .map((entry) => ({
        word: entry.word,
        explanation: entry.localized![languageCode].explanation,
        examplePhrases: entry.localized![languageCode].examplePhrases,
      }));
  };

  if (!episode) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          Episode not found
        </div>
        <Link to="/episodes" className="text-blue-600 hover:text-blue-800 mt-4 inline-block">
          &larr; Back to Episodes
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Display Localizations</h1>
          <p className="text-gray-600 mt-2">Manage display localizations for "{episode.name}"</p>
          <div className="mt-2 flex items-center gap-2">
            <span className="px-2 py-1 text-xs bg-indigo-100 text-indigo-800 rounded font-medium">
              {episode.languageCode.toUpperCase()}
            </span>
            <span className="text-sm text-gray-500">(Episode language)</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {/* Story 11.19: Import Localizations from JSON */}
          <EpisodeJsonImport
            importType="localizations"
            onLocalizationsImport={handleLocalizationsImport}
            disabled={isImporting || isGeneratingAll}
          />
          <Link to="/episodes" className="text-blue-600 hover:text-blue-800 font-medium">
            &larr; Back to Episodes
          </Link>
        </div>
      </div>

      {/* Generate New Localization */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Generate New Localization</h2>
        <p className="text-gray-600 mb-4">
          Generate AI-translated display content for users who prefer to see episode details in
          their native language.
        </p>

        {/* Dictionary info */}
        {episode.dictionary && episode.dictionary.length > 0 && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">
              <span className="font-medium">Dictionary:</span> This episode has{' '}
              {episode.dictionary.length} vocabulary{' '}
              {episode.dictionary.length === 1 ? 'entry' : 'entries'} that will also be localized.
            </p>
          </div>
        )}

        <div className="flex items-end gap-4 flex-wrap">
          <div className="flex-1 max-w-xs">
            <label className="block text-sm font-medium text-gray-700 mb-1">Target Language</label>
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="language-select"
              disabled={isGeneratingAll || isSavingAll}
            >
              <option value="">Select a language</option>
              {availableLanguages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name} ({lang.code.toUpperCase()})
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={handleAddManually}
            disabled={!selectedLanguage || isGeneratingAll || isSavingAll}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            data-testid="add-manually-button"
          >
            Add Manually
          </button>
          <button
            onClick={handleGenerate}
            disabled={
              !selectedLanguage || generateMutation.isPending || isGeneratingAll || isSavingAll
            }
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
            data-testid="generate-button"
          >
            {generateMutation.isPending && !isGeneratingAll ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Generating...
              </>
            ) : (
              'Generate One'
            )}
          </button>
          <button
            onClick={handleGenerateAll}
            disabled={
              availableLanguages.length === 0 ||
              isGeneratingAll ||
              generateMutation.isPending ||
              isSavingAll
            }
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
            data-testid="generate-all-button"
          >
            {isGeneratingAll ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Generating...
              </>
            ) : (
              `Generate All (${availableLanguages.length})`
            )}
          </button>
        </div>

        {availableLanguages.length === 0 && Object.keys(pendingLocalizations).length === 0 && (
          <p className="text-sm text-gray-500 mt-4">
            All available languages already have localizations for this episode.
          </p>
        )}
      </div>

      {/* Pending (Unsaved) Localizations */}
      {Object.keys(pendingLocalizations).length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold text-yellow-800">Pending Localizations</h2>
              <span className="px-2 py-0.5 text-xs bg-yellow-200 text-yellow-800 rounded-full font-medium">
                {Object.keys(pendingLocalizations).length} unsaved
              </span>
            </div>
            <button
              onClick={handleSaveAll}
              disabled={isSavingAll || isGeneratingAll}
              className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              data-testid="save-all-button"
            >
              {isSavingAll ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Save All ({Object.keys(pendingLocalizations).length})
                </>
              )}
            </button>
          </div>

          <p className="text-sm text-yellow-700 mb-4">
            These localizations have been generated or added but not yet saved. Click "Save All" to
            persist them.
          </p>

          <div className="space-y-3">
            {Object.values(pendingLocalizations).map((pending) => (
              <div
                key={pending.languageCode}
                className="border border-yellow-300 rounded-lg p-4 bg-white"
                data-testid={`pending-${pending.languageCode}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-1 text-sm bg-yellow-100 text-yellow-800 rounded font-medium">
                        {pending.languageCode.toUpperCase()}
                      </span>
                      <span className="text-gray-600">{getLanguageName(pending.languageCode)}</span>
                      <span className="px-2 py-0.5 text-xs bg-yellow-200 text-yellow-700 rounded">
                        Draft
                      </span>
                    </div>

                    <h3 className="font-medium text-gray-900 mb-1">{pending.localizedName}</h3>
                    <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                      {pending.localizedContext}
                    </p>

                    <div className="flex flex-wrap gap-2">
                      <span className="text-xs text-gray-500">
                        {pending.localizedGoals.length} goal
                        {pending.localizedGoals.length !== 1 ? 's' : ''}
                      </span>
                      <span className="text-gray-300">|</span>
                      <span className="text-xs text-gray-500">
                        {pending.localizedPersonas.length} persona
                        {pending.localizedPersonas.length !== 1 ? 's' : ''}
                      </span>
                      {pending.dictionaryUpdates && pending.dictionaryUpdates.length > 0 && (
                        <>
                          <span className="text-gray-300">|</span>
                          <span className="text-xs text-green-600">
                            {pending.dictionaryUpdates.length} dictionary{' '}
                            {pending.dictionaryUpdates.length !== 1 ? 'entries' : 'entry'}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEditPending(pending.languageCode)}
                      className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                      title="Edit draft"
                      data-testid={`edit-pending-${pending.languageCode}`}
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeletePending(pending.languageCode)}
                      className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                      title="Discard draft"
                      data-testid={`delete-pending-${pending.languageCode}`}
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Existing Localizations */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">Existing Localizations</h2>

        {localizations.length === 0 ? (
          <div className="text-gray-500 py-8 text-center">
            <p>No display localizations yet.</p>
            <p className="text-sm mt-2">
              Generate a localization above to help users view this episode in their native
              language.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {localizations.map((localization) => (
              <div
                key={localization.id}
                className="border border-gray-200 rounded-lg p-4"
                data-testid={`localization-${localization.languageCode}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-1 text-sm bg-blue-100 text-blue-800 rounded font-medium">
                        {localization.languageCode.toUpperCase()}
                      </span>
                      <span className="text-gray-600">
                        {getLanguageName(localization.languageCode)}
                      </span>
                    </div>

                    <h3 className="font-medium text-gray-900 mb-1">{localization.localizedName}</h3>
                    <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                      {localization.localizedContext}
                    </p>

                    <div className="flex flex-wrap gap-2 mb-2">
                      <span className="text-xs text-gray-500">
                        {localization.localizedGoals.length} goal
                        {localization.localizedGoals.length !== 1 ? 's' : ''}
                      </span>
                      <span className="text-gray-300">|</span>
                      <span className="text-xs text-gray-500">
                        {localization.localizedPersonas.length} persona
                        {localization.localizedPersonas.length !== 1 ? 's' : ''}
                      </span>
                      {getLocalizedDictionary(localization.languageCode).length > 0 && (
                        <>
                          <span className="text-gray-300">|</span>
                          <span className="text-xs text-green-600">
                            {getLocalizedDictionary(localization.languageCode).length} dictionary{' '}
                            {getLocalizedDictionary(localization.languageCode).length !== 1
                              ? 'entries'
                              : 'entry'}
                          </span>
                        </>
                      )}
                    </div>

                    {/* Localized Dictionary (if exists) */}
                    {getLocalizedDictionary(localization.languageCode).length > 0 && (
                      <details className="mt-2">
                        <summary className="text-xs text-green-700 cursor-pointer hover:text-green-800">
                          View localized dictionary
                        </summary>
                        <div className="mt-2 space-y-3 pl-2 border-l-2 border-green-200">
                          {getLocalizedDictionary(localization.languageCode).map((entry, idx) => {
                            const originalEntry = episode?.dictionary?.[idx];
                            return (
                              <div key={idx} className="text-xs">
                                <div className="font-medium text-gray-700">{entry.word}</div>
                                <div className="text-gray-600 ml-2">{entry.explanation}</div>
                                {originalEntry?.examplePhrases.map((originalPhrase, phraseIdx) => (
                                  <div key={phraseIdx} className="ml-2 mt-1">
                                    <div className="text-gray-500 italic">"{originalPhrase}"</div>
                                    {entry.examplePhrases[phraseIdx] && (
                                      <div className="text-green-700">
                                        → {entry.examplePhrases[phraseIdx]}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      </details>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(localization)}
                      className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                      title="Edit localization"
                      data-testid={`edit-${localization.languageCode}`}
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteClick(localization)}
                      className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                      title="Delete localization"
                      data-testid={`delete-${localization.languageCode}`}
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Localization Modal */}
      <LocalizationModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        onStage={handleStage}
        localization={editingLocalization || undefined}
        pendingLocalization={
          editingPendingLanguage ? pendingLocalizations[editingPendingLanguage] : undefined
        }
        generatedContent={generatedContent || undefined}
        existingDictionary={
          editingLocalization
            ? getLocalizedDictionary(editingLocalization.languageCode)
            : editingPendingLanguage &&
                pendingLocalizations[editingPendingLanguage]?.dictionaryUpdates
              ? pendingLocalizations[editingPendingLanguage].dictionaryUpdates
              : undefined
        }
        originalDictionary={
          episode?.dictionary?.map((entry) => ({
            word: entry.word,
            explanation: entry.explanation,
            examplePhrases: entry.examplePhrases,
          })) || undefined
        }
        languageCode={
          selectedLanguage || editingLocalization?.languageCode || editingPendingLanguage || ''
        }
        languageName={getLanguageName(
          selectedLanguage || editingLocalization?.languageCode || editingPendingLanguage || ''
        )}
        isLoading={
          createMutation.isPending || updateMutation.isPending || updateEpisodeMutation.isPending
        }
        isEdit={!!editingLocalization}
        isPending={!!editingPendingLanguage}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setLocalizationToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        languageName={getLanguageName(localizationToDelete?.languageCode || '')}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
